// VIPER and Summit Operations brand marks and backdrops (docs/VIPER/VIPER_BRAND.md, D-O35 / D-O36).
// Rule 3: the lettered emblem (#1) is rendered only at >= 128 px; below that use the round head mark.
// Rule 4: the VIPER name is always present as visible text next to the art (never image-only).
// Rule 5: respects prefers-reduced-motion (no animated glow or pulse).

export const VIPER_EXPANSION = "Visual Industrial Planning and Execution Resource";
export const VIPER_FULL_TITLE = `VIPER — ${VIPER_EXPANSION}`;

export type ViperMarkSize = 48 | 96 | 192;
export type ViperEmblemSize = 128 | 256 | 512;

export interface ViperMarkProps {
  /** Which generated round head mark asset to load (48, 96 or 192 px). */
  size?: ViperMarkSize;
  /** Rendered pixel width and height (defaults to size). */
  displaySize?: number;
  /** Visible text shown next to the mark. Always includes "VIPER" per VIPER_BRAND.md §5 Rule 4. */
  label?: string;
  /** Optional secondary line under the label. */
  subtitle?: string;
  /** Image alt attribute. Defaults to "" because the visible label stands right beside the image. */
  imgAlt?: string;
  /** Optional class on the outer wrapper. */
  className?: string;
  /** Optional class on the text label. */
  labelClassName?: string;
  /** Test identifier for the wrapper. */
  testId?: string;
}

/** Ensures the visible label beside any VIPER mark always names VIPER in text (VIPER_BRAND.md §5 Rule 4). */
export function ensureViperLabel(label?: string): string {
  const trimmed = label?.trim() ?? "";
  if (!trimmed) return "VIPER";
  return /viper/i.test(trimmed) ? trimmed : `VIPER · ${trimmed}`;
}

/**
 * Round VIPER head mark (`/icons/viper/viper-mark-{48,96,192}.webp`) with the VIPER name always rendered in text
 * beside it. Use this for surfaces below 128 px (panel headers, cards, badges, sign-in caption).
 */
export function ViperMark({
  size = 48,
  displaySize,
  label = "VIPER",
  subtitle,
  imgAlt = "",
  className = "",
  labelClassName = "text-xs font-medium text-slate-200",
  testId = "viper-mark"
}: ViperMarkProps) {
  const px = displaySize ?? size;
  const visibleLabel = ensureViperLabel(label);

  return (
    <span className={`inline-flex items-center gap-2 ${className}`.trim()} data-testid={testId} title={VIPER_FULL_TITLE}>
      <img
        src={`/icons/viper/viper-mark-${size}.webp`}
        alt={imgAlt}
        width={px}
        height={px}
        className="flex-none rounded-full ring-1 ring-viper-cyan/40"
      />
      <span className="inline-flex flex-col leading-tight">
        <span className={labelClassName}>{visibleLabel}</span>
        {subtitle && <span className="text-[11px] text-slate-300">{subtitle}</span>}
      </span>
    </span>
  );
}

export interface ViperEmblemProps {
  /** Which full lettered emblem asset to load (128, 256 or 512 px; never below 128 px). */
  size?: ViperEmblemSize;
  /** Rendered pixel width and height, clamped to >= 128 px per VIPER_BRAND.md §5 Rule 3. */
  displaySize?: number;
  /** Visible text shown next to the emblem. Always includes "VIPER" per VIPER_BRAND.md §5 Rule 4. */
  label?: string;
  /** Optional secondary line under the label. */
  subtitle?: string;
  /** Image alt attribute. */
  imgAlt?: string;
  /** Optional class on the outer wrapper. */
  className?: string;
  /** Test identifier for the wrapper. */
  testId?: string;
}

/**
 * Full "SUMMIT VIPER" lettered emblem (`/icons/viper/viper-emblem-{128,256,512}.webp`), never rendered below 128 px,
 * with the VIPER name always present in text beside it (wall-display header, VIPER hero, architecture detail).
 */
export function ViperEmblem({
  size = 128,
  displaySize,
  label = "VIPER",
  subtitle,
  imgAlt = "Summit VIPER emblem",
  className = "",
  testId = "viper-emblem"
}: ViperEmblemProps) {
  const px = Math.max(128, displaySize ?? size);
  const visibleLabel = ensureViperLabel(label);

  return (
    <div className={`inline-flex items-center gap-3 ${className}`.trim()} data-testid={testId}>
      <img
        src={`/icons/viper/viper-emblem-${size}.webp`}
        alt={imgAlt}
        width={px}
        height={px}
        className="flex-none rounded-lg ring-1 ring-viper-cyan/30"
      />
      <div className="leading-tight text-left">
        <div className="text-sm font-semibold tracking-wide text-slate-50">{visibleLabel}</div>
        {subtitle && <div className="text-xs text-slate-300">{subtitle}</div>}
      </div>
    </div>
  );
}

export interface SiteEmblemProps {
  /** Rendered pixel width and height. */
  size?: number;
  /** Accessible alt text for the Summit Operations site emblem. */
  alt?: string;
  /** Optional class on the image element. */
  className?: string;
  /** Test identifier for the image element. */
  testId?: string;
}

/** Summit Operations site emblem (`/favicon/apple-touch-icon.png`). */
export function SiteEmblem({
  size = 44,
  alt = "Summit Operations emblem",
  className = "h-11 w-11 flex-none rounded-full ring-1 ring-viper-cyan/40",
  testId = "site-emblem"
}: SiteEmblemProps) {
  return <img src="/favicon/apple-touch-icon.png" alt={alt} width={size} height={size} className={className} data-testid={testId} />;
}

export interface ViperHeroBackdropProps {
  /** Scrim/overlay class layered over the wireframe image (for example `bg-viper-navy/70` on the wall display). */
  overlayClassName?: string;
  /** Test identifier for the backdrop container. */
  testId?: string;
}

/**
 * Dark wireframe hero backdrop (`/images/backgrounds/viper-wireframe-hero-1376.webp`, `-960.webp` below 1024 px via
 * media query and srcset, and `-1376.jpg` fallback). Static — respects prefers-reduced-motion.
 */
export function ViperHeroBackdrop({
  overlayClassName = "bg-viper-navy/70",
  testId = "viper-hero-backdrop"
}: ViperHeroBackdropProps) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true" data-testid={testId}>
      <picture className="block h-full w-full">
        <source media="(max-width: 1023px)" srcSet="/images/backgrounds/viper-wireframe-hero-960.webp" type="image/webp" />
        <source media="(min-width: 1024px)" srcSet="/images/backgrounds/viper-wireframe-hero-1376.webp" type="image/webp" />
        <img
          src="/images/backgrounds/viper-wireframe-hero-1376.jpg"
          srcSet="/images/backgrounds/viper-wireframe-hero-960.webp 960w, /images/backgrounds/viper-wireframe-hero-1376.webp 1376w"
          sizes="100vw"
          alt=""
          className="h-full w-full object-cover object-right"
          data-testid={`${testId}-img`}
        />
      </picture>
      <div className={`absolute inset-0 ${overlayClassName}`.trim()} data-testid={`${testId}-overlay`} />
    </div>
  );
}
